import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTr, fmtDate } from "@/lib/tr";
import { Loader2, ShoppingBag, ListOrdered, Server, LifeBuoy } from "lucide-react";

function Stat({ label, value, to, testid, accent = "text-white" }) {
  const body = (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 transition-colors hover:border-accent/50" data-testid={testid}>
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`mt-1.5 font-display text-2xl font-bold ${accent}`}>{value ?? 0}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Block({ icon: Icon, title, children }) {
  return (
    <section className="mt-6">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400"><Icon className="h-4 w-4 text-accent" />{title}</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

export default function AgentDashboard() {
  const tr = useTr();
  const [me, setMe] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/agent/me").then((r) => setMe(r.data)).catch(() => setMe(false));
    api.get("/agent/dashboard").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  if (me === null || d === null) return <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-accent" /></div>;
  if (me === false) return <div className="py-20 text-center text-slate-400">{tr("Impossible de charger votre espace.", "Unable to load your space.")}</div>;
  const empty = !d.services && !d.orders && !d.providers && !d.support;
  return (
    <div data-testid="agent-dashboard">
      <h1 className="font-display text-2xl font-bold">{tr("Bonjour", "Hello")} {me.first_name || me.name}</h1>
      <p className="mt-1 text-sm text-slate-400">{me.job_title}{me.department ? ` · ${me.department}` : ""} — {tr("données en temps réel de TWP selon vos permissions.", "live TWP data for your permissions.")}</p>
      {empty && <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900 p-8 text-center text-sm text-slate-400" data-testid="agent-no-modules">{tr("Aucun module opérationnel ne vous est encore attribué. Contactez le Super Admin.", "No operational module is assigned to you yet. Contact the Super Admin.")}</div>}
      {d.services && <Block icon={ShoppingBag} title={tr("Services SMM", "SMM services")}>
        <Stat label={tr("Publiés", "Published")} value={d.services.active} to="/agent/services" testid="dash-services-active" accent="text-emerald-400" />
        <Stat label={tr("Non publiés", "Unpublished")} value={d.services.private} to="/agent/services" testid="dash-services-private" />
        <Stat label={tr("Total catalogue", "Catalogue total")} value={d.services.total} to="/agent/services" testid="dash-services-total" />
      </Block>}
      {d.orders && <Block icon={ListOrdered} title={tr("Commandes", "Orders")}>
        <Stat label={tr("En attente", "Pending")} value={d.orders.pending} to="/agent/orders" testid="dash-orders-pending" />
        <Stat label={tr("En cours", "In progress")} value={(d.orders.processing || 0) + (d.orders.in_progress || 0)} to="/agent/orders" testid="dash-orders-progress" accent="text-amber-400" />
        <Stat label={tr("En échec", "Failed")} value={d.orders.failed} to="/agent/orders" testid="dash-orders-failed" accent="text-red-400" />
        <Stat label={tr("À vérifier", "To check")} value={d.orders.uncertain} to="/agent/orders" testid="dash-orders-uncertain" accent="text-amber-400" />
      </Block>}
      {d.support && <Block icon={LifeBuoy} title={`${tr("Support", "Support")} (${(d.support.languages || []).map((l) => l.toUpperCase()).join(", ") || "—"})`}>
        <Stat label={tr("Tickets en attente", "Waiting tickets")} value={d.support.waiting} to="/agent/support" testid="dash-support-waiting" />
        <Stat label={tr("Mes tickets en cours", "My open tickets")} value={d.support.in_progress} to="/agent/support" testid="dash-support-progress" accent="text-amber-400" />
        <Stat label={tr("Réponse requise", "Needs reply")} value={d.support.needs_reply} to="/agent/support" testid="dash-support-reply" accent="text-red-400" />
        <Stat label={tr("Résolus", "Resolved")} value={d.support.resolved} to="/agent/support" testid="dash-support-resolved" accent="text-emerald-400" />
      </Block>}
      {d.providers && <Block icon={Server} title={tr("Fournisseurs", "Providers")}>
        <Stat label={tr("Fournisseurs", "Providers")} value={d.providers.total} to="/agent/providers" testid="dash-providers-total" />
        <Stat label={tr("Actifs", "Active")} value={d.providers.active} to="/agent/providers" testid="dash-providers-active" accent="text-emerald-400" />
      </Block>}
      <section className="mt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">{tr("Mes dernières actions", "My latest actions")}</h2>
        <div className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900" data-testid="dash-recent">
          {!(d.recent_activity || []).length && <div className="p-6 text-center text-sm text-slate-500">{tr("Aucune action pour le moment.", "No actions yet.")}</div>}
          {(d.recent_activity || []).map((l, i) => <div key={i} className="flex justify-between gap-3 px-4 py-2.5 text-sm"><span>{l.action} <span className="text-slate-500">· {l.entity}</span></span><span className="text-xs text-slate-500">{fmtDate(l.created_at, tr.lang)}</span></div>)}
        </div>
      </section>
    </div>
  );
}
